import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState, useRef } from 'react';
import { Camera, Upload, X } from 'lucide-react';
import { Button } from './button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface ProfilePhotoUploadProps {
  currentPhotoUrl: string | null;
  onPhotoUpdate: (url: string | null) => void;
  className?: string;
}

const compressImage = (file: File): Promise<File> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions (max 400x400 for logos)
      const maxSize = 400;
      let { width, height } = img;

      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx?.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          } else {
            resolve(file);
          }
        },
        'image/jpeg',
        0.8 // 80% quality for good balance of size/quality
      );
    };

    img.src = URL.createObjectURL(file);
  });
};

const ProfilePhotoUpload: React.FC<ProfilePhotoUploadProps> = ({
  currentPhotoUrl,
  onPhotoUpdate,
  className = ""
}) => {
  useUiLanguage();
  const [isUploading, setIsUploading] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { profile } = useAuth();

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !profile?.id) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: uiText("Error"),
        description: uiText("Please select a valid image file (JPG, PNG, GIF, or WebP)"),
        variant: "destructive"
      });
      return;
    }

    // Validate file size (2MB limit for faster loading)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: uiText("Error"),
        description: uiText("Image size must be less than 2MB for optimal performance"),
        variant: "destructive"
      });
      return;
    }

    // Validate file name
    if (!file.name || file.name.length > 100) {
      toast({
        title: uiText("Error"),
        description: uiText("Invalid file name"),
        variant: "destructive"
      });
      return;
    }

    setIsUploading(true);

    try {
      // Compress image for better performance
      const compressedFile = await compressImage(file);

      // Generate unique filename with proper extension handling
      const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const fileName = `${profile.id}-${Date.now()}.${fileExt}`;
      const filePath = `${profile.id}/${fileName}`;

      // Delete existing photo if it exists
      if (currentPhotoUrl) {
        const existingPath = currentPhotoUrl.split('/').pop();
        if (existingPath) {
          await supabase.storage
            .from('profile-photos')
            .remove([`${profile.id}/${existingPath}`]);
        }
      }

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('profile-photos')
        .upload(filePath, compressedFile, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        console.error('Upload error details:', uploadError);
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('profile-photos')
        .getPublicUrl(filePath);

      // Update profile with new photo URL
      await onPhotoUpdate(publicUrl);

      toast({
        title: uiText("Success"),
        description: uiText("Profile photo updated successfully")
      });

    } catch (error: any) {
      console.error('Error uploading photo:', error);
      let errorMessage = uiText("Failed to upload profile photo");

      if (error?.message) {
        if (error.message.includes('Policy')) {
          errorMessage = uiText("You don't have permission to upload photos");
        } else if (error.message.includes('size')) {
          errorMessage = uiText("Image file is too large");
        } else if (error.message.includes('type')) {
          errorMessage = uiText("Invalid file type. Please use JPG, PNG, or GIF");
        } else {
          errorMessage = `Upload failed: ${error.message}`;
        }
      }

      toast({
        title: uiText("Error"),
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
      setShowOverlay(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemovePhoto = async () => {
    setIsUploading(true);
    try {
      await onPhotoUpdate(null);
      toast({
        title: uiText("Success"),
        description: uiText("Profile photo removed successfully")
      });
    } catch (error) {
      console.error('Error removing photo:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to remove profile photo"),
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
      setShowOverlay(false);
    }
  };

  // Check if this is being used as a standalone button (not overlaying an image)
  const isStandaloneButton = className && className.includes('standalone');

  if (isStandaloneButton) {
    return (
      <div className={`flex gap-2 ${className}`}>
        <Button
          size="sm"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="flex items-center gap-2"
        >
          {isUploading ? (
            <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
          {uiText("Upload Logo")}</Button>
        {currentPhotoUrl && (
          <Button
            size="sm"
            variant="destructive"
            onClick={handleRemovePhoto}
            disabled={isUploading}
            className="flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            {uiText("Remove")}</Button>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
    );
  }

  return (
    <div
      className={`relative group cursor-pointer ${className}`}
      onMouseEnter={() => setShowOverlay(true)}
      onMouseLeave={() => setShowOverlay(false)}
    >
      {/* Overlay with upload options */}
      {showOverlay && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-full z-10">
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="p-2"
            >
              {isUploading ? (
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <Camera className="w-4 h-4" />
              )}
            </Button>
            {currentPhotoUrl && (
              <Button
                size="sm"
                variant="destructive"
                onClick={handleRemovePhoto}
                disabled={isUploading}
                className="p-2"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

export default ProfilePhotoUpload;